import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { Gate, InsertGateBody, UpdateGateBody } from "./types/gates";

export const useGatesApi = () => {
  const api = useApi();

  const getAdminGate = useCallback(
    (gateId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/gates/${gateId}`;
      return useSWRMutation<Gate, CustomError, string>(url, (url) => api.get<Gate>({ url, signal }));
    },
    [api],
  );

  const useAdminGates = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/gates`, query);
      return useSWRImmutable<Paginated<Gate>, CustomError, string>(url, (url) =>
        api.get<Paginated<Gate>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminGates = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/gates`, query);
      return api.get<Paginated<Gate>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminGate = useCallback(
    async (body: InsertGateBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/gates`;
      const id = await api.post<{ id: string }, InsertGateBody>(url, body);
      mutateCache([url, ...body.systemIds.map((systemId) => `/admin/settings/border/systems/${systemId}/gates`)]);
      return id;
    },
    [api],
  );

  const updateAdminGate = useCallback(
    async (gate: Gate, body: UpdateGateBody): Promise<void> => {
      const url = `/admin/settings/border/gates/${gate.item.id}`;
      await api.put<UpdateGateBody>(url, body);

      const systemIds = [...new Set([...gate.systems.map((system) => system.id), ...body.systemIds])];

      mutateCache([
        url,
        `/admin/settings/border/gates`,
        ...systemIds.map((systemId) => `/admin/settings/border/systems/${systemId}/gates`),
      ]);
    },
    [api],
  );

  const updateAdminGateVisibility = useCallback(
    async (gateId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/gates/${gateId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility), mutateCache([`/admin/settings/border/gates`]));
    },
    [api],
  );

  const updateAdminGateOrder = useCallback(
    async (gateId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/gates/${gateId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order), mutateCache([`/admin/settings/border/gates`]));
    },
    [api],
  );

  const deleteAdminGate = useCallback(
    async (gate: Gate): Promise<void> => {
      const url = `/admin/settings/border/gates/${gate.item.id}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/border/gates`,
        ...gate.systems.map((system) => `/admin/settings/border/systems/${system.id}/gates`),
      ]);
    },
    [api],
  );

  return {
    getAdminGate,
    insertAdminGate,
    deleteAdminGate,
    useAdminGates,
    getAdminGates,
    updateAdminGate,
    updateAdminGateVisibility,
    updateAdminGateOrder,
  };
};
